import { useState, useEffect, useCallback } from "react";
import styles from "./Company.module.css";
import { FaFilter } from "react-icons/fa";
import CreateCompanyModal from "./modal/CreateCompanyModal";
import CompanyDetailModal from "./modal/CompanyDetailModal";
import { API_URL } from "../../config/api";

function Company() {
  const [companies, setCompanies] = useState([]);
  const [showExtraFilters, setShowExtraFilters] = useState(false);
  const [filterDate, setFilterDate] = useState("");
  const [search, setSearch] = useState("");
  const [filterBIN, setFilterBIN] = useState("");
  const [filterOwnerNumber, setFilterOwnerNumber] = useState("");
  const [filterOwnerName, setFilterOwnerName] = useState("");

  const [filterAccount] = useState("");
  const [filterTIS] = useState("");

  const [filterTariffEnd, setFilterTariffEnd] = useState("");

  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [openDetailModal, setOpenDetailModal] = useState(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);

  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");

  const token = localStorage.getItem("token");

  const fetchCategories = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/org_categories`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) throw new Error("Ошибка получения категорий");

      const data = await res.json();
      setCategories(data.data || []);
    } catch (err) {
      console.error("Ошибка загрузки категорий:", err);
      setCategories([]);
    }
  }, [token]);

  const fetchCompanies = useCallback(async () => {
    try {
      const query = selectedCategory ? `?category_id=${selectedCategory}` : "";

      const res = await fetch(`${API_URL}/api/orgs${query}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) throw new Error("Ошибка получения компаний");

      const data = await res.json();

      const categoryMap = {};
      categories.forEach((cat) => {
        categoryMap[cat.id] = cat.title;
      });

      const mapped = (data.data || []).map((c) => ({
        ...c,
        name: c.name,
        bin: c.sn || "-",
        ownerNumber: c.ownerNumber || "-",
        ownerName: c.ownerName || "-",
        account: c.account || "-",
        tis: c.tis || "-",
        tariffEnd: c.tariffEnd || "-",
        createdAt: c.inserted_at?.split("T")[0],
        sphere: categoryMap[c.category_id] || "-",
      }));

      setCompanies(mapped);
    } catch (error) {
      console.error("Ошибка при загрузке компаний:", error);
      setCompanies([]);
    }
  }, [selectedCategory, token, categories]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  useEffect(() => {
    if (categories.length > 0) {
      fetchCompanies();
    }
  }, [categories.length, fetchCompanies]);

  const filteredCompanies = companies.filter((c) => {
    const matchesSearch = c.name.toLowerCase().includes(search.toLowerCase());
    const matchesDate = filterDate ? c.createdAt === filterDate : true;
    const matchesBIN = filterBIN ? c.bin.includes(filterBIN) : true;

    const matchesOwnerNumber = filterOwnerNumber
      ? String(c.ownerNumber).includes(filterOwnerNumber)
      : true;

    const matchesOwnerName = filterOwnerName
      ? String(c.ownerName).toLowerCase().includes(filterOwnerName.toLowerCase())
      : true;

    const matchesAccount = filterAccount ? String(c.account).includes(filterAccount) : true;
    const matchesTIS = filterTIS ? String(c.tis).includes(filterTIS) : true;

    const matchesTariffEnd = filterTariffEnd
      ? String(c.tariffEnd).includes(filterTariffEnd)
      : true;

    return (
      matchesSearch &&
      matchesDate &&
      matchesBIN &&
      matchesOwnerNumber &&
      matchesOwnerName &&
      matchesAccount &&
      matchesTIS &&
      matchesTariffEnd
    );
  });

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.headerText}>Компании</h1>
      </div>

      <div className={styles.filters}>
        <div className={styles.mainFilters}>
          <input
            type="text"
            placeholder="Название"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={styles.input}
          />

          <input
            type="text"
            placeholder="БИН"
            value={filterBIN}
            onChange={(e) => setFilterBIN(e.target.value)}
            className={styles.input}
          />

          <select
            className={styles.input}
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
          >
            <option value="">Сфера деятельности</option>
            {categories.map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.title}
              </option>
            ))}
          </select>

          <button className={styles.btn} onClick={() => setOpenCreateModal(true)}>
            + Создать
          </button>

          <button
            className={styles.moreFiltersBtn}
            onClick={() => setShowExtraFilters((p) => !p)}
          >
            <FaFilter size={18} />
          </button>
        </div>

        {showExtraFilters && (
          <div className={styles.extraFilters}>
            <div className={styles.grid}>
              <div className={styles.dateBlock}>
                <label>Введите телефон:</label>
                <input
                  type="text"
                  placeholder="Номер владельца"
                  value={filterOwnerNumber}
                  onChange={(e) => setFilterOwnerNumber(e.target.value)}
                  className={styles.input}
                />
              </div>

              <div className={styles.dateBlock}>
                <label>Введите имя:</label>
                <input
                  type="text"
                  placeholder="Владелец"
                  value={filterOwnerName}
                  onChange={(e) => setFilterOwnerName(e.target.value)}
                  className={styles.input}
                />
              </div>

              <div className={styles.dateBlock}>
                <label>Дата создания:</label>
                <input
                  type="date"
                  value={filterDate}
                  onChange={(e) => setFilterDate(e.target.value)}
                  className={styles.input}
                />
              </div>

              <div className={styles.dateBlock}>
                <label>Тариф истекает:</label>
                <input
                  type="date"
                  value={filterTariffEnd}
                  onChange={(e) => setFilterTariffEnd(e.target.value)}
                  className={styles.input}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Название</th>
              <th>БИН</th>
              <th>Номер владельца</th>
              <th>Владелец</th>
              <th>Дата создания</th>
              <th>Тариф истекает</th>
              <th>Сфера деятельности</th>
            </tr>
          </thead>

          <tbody>
            {filteredCompanies.map((c) => (
              <tr
                key={c.id}
                onClick={() => {
                  setSelectedCompanyId(c.id);
                  setOpenDetailModal(true);
                }}
                style={{ cursor: "pointer" }}
              >
                <td>{c.name}</td>
                <td>{c.bin}</td>
                <td>{c.ownerNumber}</td>
                <td>{c.ownerName}</td>
                <td>{c.createdAt}</td>
                <td>{c.tariffEnd}</td>
                <td>{c.sphere}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {filteredCompanies.length === 0 && <p>Компании не найдены</p>}
      </div>

      <CreateCompanyModal
        open={openCreateModal}
        onClose={() => setOpenCreateModal(false)}
        onCreated={fetchCompanies}
      />

      <CompanyDetailModal
        open={openDetailModal}
        onClose={() => setOpenDetailModal(false)}
        companyId={selectedCompanyId}
      />
    </div>
  );
}

export default Company;
